/* global define */
/**
 * The abstract base class for models fetched from LS Cloud endpoints
 *
 * Copyright 2014 LightSpeed Retail. All rights reserved.
 * Created by acastruc on 2014-03-13.
 */
import Backbone from 'backbone';
import Filter from 'app/src/data/models/utils/Filter';
import AuthValidator from 'app/router/AuthValidator';
import _ from 'underscore';

export default class RetailModel extends Backbone.Model {
  /**
   * Returns an instance of the given class based on the relation specified iff this model was loaded
   * with that relationship
   * If the specified relationship was not loaded or doesn't exist then null is returned.
   * @param relationName string the name of the relation to query
   * @param ModelClass - the class to instantiate for the relation
   * @returns {null or Backbone.Model}
   */
  getRelation(relationName, ModelClass) {
    if (this.has(relationName)) {
      let relationJSON = this.get(relationName);
      return new ModelClass(relationJSON, { parse: true });
    }
    return null;
  }

  /**
   *  Save the given model as the given relational property. Clears the relational property if passed
   *  null.
   *
   *  @param relationName {string} the name of the relation to set
   *  @param model {Backbone.Model} the model to serialize as the relation value
   */
  setRelation(relationName, model) {
    if (model) {
      this.set(relationName, model.toJSON());
    } else {
      this.unset(relationName);
    }
  }

  /**
   * Override the fetch method to optionally pass a Filter as a parameter,
   * in addition to the existing options parameter
   *
   * @param filter {Filter} optional filter to apply to the fetch
   * @param options {object} optional hash of additional settings to pass to the fetch
   * @return {Promise} that resolves when the fetch succeeds and fails otherwise
   */
  fetch(filter, options = {}) {
    if (filter instanceof Filter) {
      options.data = filter.getQueryParameters();
    } else {
      options = filter;
    }

    let promise = super.fetch(options);

    promise.fail(jqXHR => {
      AuthValidator.validateStatus(jqXHR.status);
    });

    return promise;
  }

  /**
   * Override the fetch method to optionally perform the operation with a response that
   * loads relations of the entity.  This is necessary in particular for PUT operations
   * on models that need all the relations loaded.
   *
   * @param withRelations {Boolean}   When true, the URL will be appended with a parameter
   *                                  to indicate that the request requires all relations
   *                                  to be loaded.
   * @param key   {Object|*}          optional hash with the attributes to set on the model
   *                                  before sending data to the sever or a key for the value
   *                                  to be set (the value parameter will need to contain
   *                                  the value)
   * @param value {*}                 optional value to be set on the model before sending data
   *                                  to the server.  The key is specified by the key parameter.
   * @param options                   option has of additional settings to pass to the save
   * @returns {*}
   */
  save(withRelations = false, key = null, value = null, options = {}) {
    if (withRelations) {
      options.url = `${this.url()}?load_relations=all`;
    }

    let promise;

    // two ways of calling save
    if (key === null) {
      promise = super.save(key, options);
    } else {
      promise = super.save(key, value, options);
    }

    promise.fail(jqXHR => {
      AuthValidator.validateStatus(jqXHR.status);
    });

    return promise;
  }

  /**
   * Overrides the default parse implementation to allow for using the retailResponseName
   * property to apply automatic filtering of the API response.
   *
   * @param {Object} response     The JSON response from the API request
   * @returns {*}
   */
  parse(response) {
    let parsedResponse = response;

    let retailResponseName = _.result(this, 'retailResponseName');
    if (response[retailResponseName]) {
      parsedResponse = response[retailResponseName];
    } else {
      parsedResponse = response;
    }

    return parsedResponse;
  }
}
